import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    CheckBadgeIcon,
    ShieldCheckIcon,
    BanknotesIcon,
    CpuChipIcon,
    GlobeAltIcon,
    TicketIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/solid';

const API_URL = '/api/v1';

export default function VerifyCertificate() {
    const { id } = useParams();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API_URL}/certificates/${id}/public`)
            .then(res => setCert(res.data))
            .catch(err => {
                console.error(err);
                setError("Certificate not found or invalid signature.");
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest animate-pulse">Consultation du Registre Sécurisé...</p>
        </div>
    );

    if (error) return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6">
            <div className="max-w-md w-full bg-white border border-red-100 shadow-2xl p-8 text-center rounded-sm">
                <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ShieldCheckIcon className="w-10 h-10" />
                </div>
                <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">Échec de la Vérification</h1>
                <p className="text-sm text-slate-500 mb-8">{error}</p>
                <Link to="/" className="inline-block bg-slate-800 text-white px-8 py-3 rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all">
                    Retour au Portail
                </Link>
            </div>
        </div>
    );

    // Logic for dynamic Law Framework
    const isSelfConsumption = cert.usage_category === 'SELF_CONSUMPTION';
    const lawText = isSelfConsumption ? 'Loi 82-21 (Autoproduction)' : 'Loi 13-09 (Énergies Renouvelables)';

    // Date formatter for Europe/Morocco (DD/MM/YYYY)
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        return new Date(dateString).toLocaleDateString('fr-FR', options);
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] py-20 px-6 font-sans antialiased">
            <div className="max-w-3xl mx-auto">
                {/* Verification Banner */}
                <div className="bg-emerald-600 text-white p-4 rounded-t-sm flex items-center justify-center gap-2 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/5 opacity-20 pointer-events-none" />
                    <CheckBadgeIcon className="w-6 h-6" />
                    <span className="text-[11px] font-black uppercase tracking-[0.2em]">Vérification du Registre Officiel Confirmée</span>
                </div>

                <div className="bg-white shadow-2xl overflow-hidden border border-slate-200">
                    <div className="p-10">
                        {/* Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start gap-6 border-b border-slate-100 pb-10 mb-10">
                            <div>
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.3em] mb-2">Actif Authentique</p>
                                <h1 className="text-4xl font-black text-slate-900 tracking-tighter uppercase leading-none">
                                    Garantie <br />d'Origine
                                </h1>
                                <p className="mt-4 text-xs font-bold text-slate-500 uppercase tracking-widest">GS1 GIAI: <span className="font-mono text-slate-900 border-b border-emerald-200">{cert.gs1_id}</span></p>
                            </div>
                            <div className="text-right">
                                <div className="bg-slate-50 p-6 border border-slate-100 rounded-sm inline-block">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Valeur Nominale</p>
                                    <p className="text-3xl font-black text-slate-900 leading-none">1 MWh</p>
                                </div>
                            </div>
                        </div>

                        {/* Content Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="space-y-8">
                                <div>
                                    <h3 className="flex items-center gap-2 text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">
                                        <CpuChipIcon className="w-4 h-4 text-emerald-600" /> Spécifications Techniques
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Source d'Énergie</p>
                                            <p className="text-sm font-bold text-slate-800">{cert.energy_source_code.includes('SOLAR') || cert.energy_source_code.includes('F01') ? 'Solaire Photovoltaïque' : 'Énergies Renouvelables'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Centrale de Production</p>
                                            <p className="text-sm font-bold text-slate-800">{cert.device_name || 'MAP-GO Verified Asset'}</p>
                                        </div>
                                        <div className="flex gap-10">
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Volume Total (Lot)</p>
                                                <p className="text-sm font-bold text-slate-800">{parseFloat(cert.output_mwh)} MWh</p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cadre Juridique</p>
                                                <p className="text-xs font-black text-indigo-600 underline decoration-indigo-200">{lawText}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="flex items-center gap-2 text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">
                                        <GlobeAltIcon className="w-4 h-4 text-emerald-600" /> Conformité Souveraine
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pays d'Origine</p>
                                            <p className="text-sm font-bold text-slate-800">Morocco / Maroc (MA)</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Autorité d'Émission</p>
                                            <p className="text-sm font-bold text-slate-800 italic underline decoration-slate-200 underline-offset-4">Ministère de la Transition Énergétique (MTEDD)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-8 bg-slate-50 p-8 border border-slate-100 relative">
                                <div className="absolute top-0 right-0 p-4">
                                    <TicketIcon className="w-12 h-12 text-slate-200 opacity-50" />
                                </div>

                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Statut du Certificat</p>
                                    <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 w-fit">
                                        <CheckBadgeIcon className="w-3.5 h-3.5" /> {cert.status === 'ISSUED' ? 'ÉMIS' : cert.status}
                                    </span>
                                </div>

                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Période de Production</p>
                                    <p className="text-sm font-bold text-slate-800 leading-tight">
                                        {formatDate(cert.production_start)} — {formatDate(cert.production_end)}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Expiration (RED II)</p>
                                    <p className="text-sm font-bold text-red-600 italic">
                                        {cert.expiry_date ? formatDate(cert.expiry_date) : formatDate(new Date(new Date().setMonth(new Date().getMonth() + 18)))}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-slate-200">
                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Empreinte Numérique (SHA-256)</p>
                                    <p className="text-[9px] font-mono font-bold text-slate-500 break-all leading-tight italic uppercase">
                                        {cert.certificate_id.replace(/-/g, '').toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-12 flex flex-col md:flex-row gap-4">
                            <a
                                href={`${API_URL}/certificates/${id}/pdf`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 bg-[#0f172a] text-white py-4 rounded-sm font-black text-[11px] uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-3 shadow-xl"
                            >
                                <ArrowDownTrayIcon className="w-5 h-5 text-emerald-400" /> Télécharger le PDF Officiel
                            </a>
                            <Link
                                to="/register"
                                className="flex-1 bg-white border border-slate-200 text-slate-700 py-4 rounded-sm font-black text-[11px] uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center"
                            >
                                Ouvrir un Compte Registre
                            </Link>
                        </div>
                    </div>
                </div>

                <p className="mt-10 text-center text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] leading-relaxed">
                    Ce service de vérification est fourni par le Registre National de l'Énergie du Maroc.<br />
                    Tous les actifs sont strictement audités par le Ministère de la Transition Énergétique.
                </p>
            </div>
        </div>
    );
}